// src/routes/lazyWithRetry.ts
import { lazy, ComponentType } from 'react'

// Loading a route chunk can fail for a moment (flaky network, a request that raced an auth
// refresh) or for good (the hashed file is gone after a deploy). Retry the import first, and
// if it still fails reload once so the browser picks up the current index.html and the chunk
// names that go with it. The user ends up on the page they clicked instead of an error screen.
const RELOAD_STAMP = 'trm-chunk-reload-at'
// At most one reload per window: a chunk that is broken on the server must not loop the app.
const RELOAD_COOLDOWN_MS = 5 * 60 * 1000
const RETRY_DELAYS = [300, 1000]

type Loader<T> = () => Promise<{ default: T }>

// Covers webpack's ChunkLoadError plus the native/module-script wordings, and the case where a
// gateway answers a .js request with an HTML login page (the script then fails to parse).
export function isChunkLoadError(error: unknown): boolean {
  const { name, message } = (error ?? {}) as { name?: string; message?: string }
  return (
    name === 'ChunkLoadError' ||
    /Loading chunk|Loading CSS chunk|dynamically imported module|Importing a module script failed|Unexpected token '<'|expected expression, got '<'/i.test(message ?? '')
  )
}

// sessionStorage can throw (private mode, blocked storage) - never let that break a route.
function lastReloadAt(): number {
  try {
    return Number(sessionStorage.getItem(RELOAD_STAMP)) || 0
  } catch {
    return 0
  }
}

// Reloads for a chunk failure unless one was already tried recently, and reports whether a
// reload is on its way so callers know if they still have to show something.
export function reloadForChunkError(): boolean {
  const now = Date.now()
  if (now - lastReloadAt() < RELOAD_COOLDOWN_MS) {
    return false
  }
  try {
    sessionStorage.setItem(RELOAD_STAMP, String(now))
  } catch {
    // ignore - worst case we reload again on the next failure
  }
  window.location.reload()
  return true
}

const wait = (ms: number) => new Promise(resolve => setTimeout(resolve, ms))

export function lazyWithRetry<T extends ComponentType<any>>(loader: Loader<T>) {
  let loadedModule: { default: T } | undefined
  const load = () => loader().then(module => {
    loadedModule = module
    return module
  })

  const loadWithRetry = async () => {
    for (let attempt = 0; ; attempt++) {
      try {
        return await load()
      } catch (error) {
        if (!isChunkLoadError(error) || attempt >= RETRY_DELAYS.length) {
          if (isChunkLoadError(error) && reloadForChunkError()) {
            // Keep the Suspense fallback up until the reload takes over: resolving or
            // rejecting here would flash a page or the error screen on the way out.
            return new Promise<{ default: T }>(() => {})
          }
          throw error
        }
        await wait(RETRY_DELAYS[attempt])
      }
    }
  }

  const Component = lazy(() => {
    // Already loaded (e.g. by preload): resolve synchronously so React renders without suspending.
    if (loadedModule) {
      const module = loadedModule
      return { then: (resolve: (value: { default: T }) => void) => resolve(module) } as unknown as Promise<{ default: T }>
    }
    return loadWithRetry()
  })
  // Loads the page ahead of navigation; failures are ignored (the lazy load above retries).
  const preload = (): Promise<void> => load().then(() => {}, () => {})
  return Object.assign(Component, { preload })
}
